import type {
  CreateHostPersonaRequest,
  HostPersona,
  UpdateHostPersonaRequest,
} from '@musedrops/shared';
import { fromDbKeys, toDbKeys, snakeCase } from '@musedrops/shared';
import { supabase } from '../services/supabase';

export interface ListHostPersonaOptions {
  page?: number;
  perPage?: number;
  sortField?: keyof HostPersona | string;
  sortOrder?: 'ASC' | 'DESC';
  filter?: Record<string, unknown>;
  withTotal?: boolean;
}

export async function listHostPersonas(
  options: ListHostPersonaOptions = {}
): Promise<{ data: HostPersona[]; total?: number }> {
  const {
    page = 1,
    perPage,
    sortField,
    sortOrder = 'ASC',
    filter,
    withTotal = false,
  } = options;

  const selectOptions = withTotal ? { count: 'exact' as const } : undefined;

  let query = supabase.from('host_personas').select('*', selectOptions);

  if (typeof perPage === 'number') {
    const start = (page - 1) * perPage;
    const end = start + perPage - 1;
    query = query.range(start, end);
  }

  const normalizedFilter = filter ? toDbKeys(filter) : undefined;
  if (normalizedFilter) {
    Object.entries(normalizedFilter).forEach(([key, value]) => {
      if (value === undefined || value === null || value === '') {
        return;
      }
      if (Array.isArray(value)) {
        query = query.in(key, value as unknown[]);
        return;
      }
      query = query.eq(key, value as string | number | boolean);
    });
  }

  if (sortField) {
    query = query.order(snakeCase(String(sortField)), {
      ascending: sortOrder === 'ASC',
    });
  } else {
    query = query.order('key', { ascending: true });
  }

  const { data, error, count } = await query;

  if (error) {
    throw new Error(`Failed to fetch host personas: ${error.message}`);
  }

  return {
    data: (data ?? []).map(row =>
      fromDbKeys<HostPersona>(row as Record<string, unknown>)
    ),
    total: withTotal ? (count ?? 0) : undefined,
  };
}

export async function getHostPersona(id: string): Promise<HostPersona> {
  const { data, error } = await supabase
    .from('host_personas')
    .select('*')
    .eq('id', id)
    .single();

  if (error) {
    throw new Error(`Failed to fetch host persona: ${error.message}`);
  }

  if (!data) {
    throw new Error('Host persona not found');
  }

  return fromDbKeys<HostPersona>(data as Record<string, unknown>);
}

export async function createHostPersona(
  personaData: CreateHostPersonaRequest
): Promise<HostPersona> {
  const { data, error } = await supabase
    .from('host_personas')
    .insert(toDbKeys(personaData))
    .select('*')
    .single();

  if (error) {
    throw new Error(`Failed to create host persona: ${error.message}`);
  }

  return fromDbKeys<HostPersona>(data as Record<string, unknown>);
}

export async function updateHostPersona(
  id: string,
  personaData: UpdateHostPersonaRequest
): Promise<HostPersona> {
  const { data, error } = await supabase
    .from('host_personas')
    .update(toDbKeys(personaData))
    .eq('id', id)
    .select('*')
    .single();

  if (error) {
    throw new Error(`Failed to update host persona: ${error.message}`);
  }

  return fromDbKeys<HostPersona>(data as Record<string, unknown>);
}

export async function deleteHostPersona(id: string): Promise<void> {
  const { error } = await supabase.from('host_personas').delete().eq('id', id);

  if (error) {
    throw new Error(`Failed to delete host persona: ${error.message}`);
  }
}

export async function getAllHostPersonas(): Promise<HostPersona[]> {
  const { data } = await listHostPersonas();
  return data;
}
